import type React from 'react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ActionBar,
    ImportFailureAlert,
    ImportTabRows,
    ImportTabs,
    IssuesSummary,
    ProductsStepHeader,
    RecheckFailureAlert,
    SuggestedFixesDialog,
} from './components';
import type { UseProductsStepResult } from './useProductsStep';

type ProductsStepViewProps = Omit<
    UseProductsStepResult,
    'hasPreview' | 'hasRecord' | 'onRequestRecheck' | 'onGroupFixApplied'
> & {
    /**
     * The suggested-fixes dialog says the whole import can be undone on
     * Result: the wizard, where Result offers Undo.
     */
    showUndoNote: boolean;
};

/**
 * Products: the header (`ProductsStepHeader`: the title and what the
 * statuses mean, the file with Re-check), why the last Re-check did not
 * answer, the issues summary (`IssuesSummary`: each kind of problem with
 * its bulk fix and Show rows, and Undo last change), the tabs over the
 * table, which shows the selected tab's rows with each row's
 * Exclude / Include (on Excluded, under what an excluded row means and
 * Include all back: `ExcludedRowsBar`), and the sticky action bar (← Names, Exclude all,
 * Import, and Import with suggested fixes beside the blocked Import while
 * rows need attention, whose summary opens in `SuggestedFixesDialog`). The branch heads the column above it, in the wizard's frame or
 * the in-shell page's header. The step's bottom padding is the bar's, so the
 * bar keeps it when it sticks.
 *
 * On a phone (`isNarrow`) the tab shows its rows as cards instead of the
 * grid, and the bar's buttons share a line with short labels and an
 * icon-only Back.
 *
 * While the import runs (`isImporting`) Re-check, every row's press and
 * button and the bar's buttons, Back too (`canGoBack`), wait; the tabs
 * still switch. Why the last import did not go through (`importSubmit`)
 * sits above the bar, with Retry.
 *
 * A row's press (`onOpenRow`) opens the row drawer, which the container
 * mounts beside this View.
 */
export const ProductsStepView: React.FC<ProductsStepViewProps> = ({
    rows,
    totalRows,
    tab,
    tabCounts,
    onTabChange,
    issues,
    issueFilter,
    onShowIssueRows,
    isFixDisabled,
    onApplyFix,
    canUndo,
    onUndo,
    fileName,
    rowCount,
    lastCheckedAt,
    isChecking,
    recheck,
    onRecheck,
    onOpenRow,
    onExcludeRow,
    onIncludeRow,
    readyCount,
    needsAttentionCount,
    uncheckedCount,
    importBlocker,
    onExcludeAll,
    onIncludeAll,
    onImport,
    canSuggestFixes,
    isSuggestDisabled,
    suggestedFixes,
    onSuggestFixes,
    onConfirmSuggestedFixes,
    onReviewRows,
    onBack,
    canGoBack,
    isImporting,
    importSubmit,
    isNarrow,
    showUndoNote,
}) => (
    <section
        className="flex flex-col gap-4 pt-4 sm:pt-6"
        {...getNodeTestIdProp('catalog-import-products')}
    >
        <ProductsStepHeader
            fileName={fileName}
            isChecking={isChecking}
            isDisabled={isImporting}
            lastCheckedAt={lastCheckedAt}
            onRecheck={onRecheck}
            rowCount={rowCount}
        />

        <RecheckFailureAlert recheck={recheck} />

        <IssuesSummary
            canUndo={canUndo}
            isFixDisabled={isFixDisabled}
            issueFilter={issueFilter}
            issues={issues}
            needsAttentionCount={needsAttentionCount}
            onApplyFix={onApplyFix}
            onShowIssueRows={onShowIssueRows}
            onUndo={onUndo}
        />

        <ImportTabs counts={tabCounts} onTabChange={onTabChange} tab={tab}>
            <ImportTabRows
                excludedCount={tabCounts.excluded}
                isDisabled={isImporting}
                isNarrow={isNarrow}
                onExcludeRow={onExcludeRow}
                onIncludeAll={onIncludeAll}
                onIncludeRow={onIncludeRow}
                onOpenRow={onOpenRow}
                rows={rows}
                tab={tab}
                totalRows={totalRows}
            />
        </ImportTabs>

        <ActionBar
            blocker={importBlocker}
            importingCount={
                suggestedFixes.status === 'ready'
                    ? suggestedFixes.summary.rows
                    : undefined
            }
            isBackDisabled={!canGoBack}
            isNarrow={isNarrow}
            needsAttentionCount={needsAttentionCount}
            notice={
                <ImportFailureAlert
                    isRetryDisabled={importBlocker !== null}
                    onRetry={onImport}
                    submit={importSubmit}
                />
            }
            onBack={onBack}
            onExcludeAll={onExcludeAll}
            onImport={onImport}
            readyCount={readyCount}
            suggestFixes={
                canSuggestFixes
                    ? {
                          isDisabled: isSuggestDisabled,
                          onPress: onSuggestFixes,
                      }
                    : null
            }
            uncheckedCount={uncheckedCount}
        />

        <SuggestedFixesDialog
            isImporting={isImporting}
            onConfirm={onConfirmSuggestedFixes}
            onRetry={onSuggestFixes}
            onReview={onReviewRows}
            showUndoNote={showUndoNote}
            state={suggestedFixes}
        />
    </section>
);
